import React, { useState } from 'react';

import { Alert, Button, Modal, Text } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { CUSTOMER_IMPERSONATION } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useAccountImpersonateCustomerLinkCreateMutation } from '@admin/schema';

const ConfirmationModal: React.FC<{ customerID: string; onClose: () => void }> = ({ customerID, onClose }) => {
  // Pundit guard: CustomerImpersonationPolicy#create? (accountImpersonateCustomerLinkCreate, ENG-740)
  const [execute, { loading }] = useAccountImpersonateCustomerLinkCreateMutation({
    client,
    variables: { customerID },
  });
  const [error, setError] = useState<string | undefined>(undefined);

  const onConfirm = async () => {
    setError(undefined);
    const { data, errors } = await execute();

    setError(errors && errors[0].message);

    const url = data?.result.url;
    if (url) {
      open(url);
      onClose();
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Access Customer Account</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
          <Text>
            ATTENTION: You are logging into the customer’s actual account portal. Any changes you make will be seen by
            the customer.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={loading} size="lg">
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={loading} size="lg" kind="danger">
            Access Customer Account
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export const AccessCustomerAccountButton: React.FC<{
  customerID: string;
}> = ({ customerID }) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  return (
    <Permission resource={CUSTOMER_IMPERSONATION.resource} action={CUSTOMER_IMPERSONATION.actions.create} denied={null}>
      {/* Pundit guard: CustomerImpersonationPolicy#create? (accountImpersonateCustomerLinkCreate, ENG-740) */}
      <Button kind="default" onClick={() => setShowModal(true)}>
        Access Customer Account
      </Button>
      {showModal && <ConfirmationModal customerID={customerID} onClose={() => setShowModal(false)} />}
    </Permission>
  );
};
